import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 20,
    backgroundColor: '#ffffff',
    fontFamily: 'Helvetica',
  },
  ticketContainer: {
    borderWidth: 1.5,
    borderColor: '#e2e8f0',
    borderRadius: 12,
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  header: {
    backgroundColor: '#0f172a',
    padding: 12,
    alignItems: 'center',
  },
  headerText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    marginBottom: 4,
  },
  eventTitle: {
    color: '#38bdf8',
    fontSize: 13,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  
  // Detalhes do Evento
  eventDetails: {
    padding: 10,
    borderBottomWidth: 1.5,
    borderBottomColor: '#f1f5f9',
    backgroundColor: '#f8fafc',
    alignItems: 'center',
  },
  eventSubtext: {
    fontSize: 8,
    color: '#64748b',
    marginTop: 2,
  },

  // Linha de Corte (Ticket Stub)
  stubLineContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: 10,
    height: 10,
  },
  stubDotLeft: {
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: '#ffffff',
    borderWidth: 1.5,
    borderColor: '#e2e8f0',
    position: 'absolute',
    left: -8,
  },
  stubDotRight: {
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: '#ffffff',
    borderWidth: 1.5,
    borderColor: '#e2e8f0',
    position: 'absolute',
    right: -8,
  },
  stubDashed: {
    flex: 1,
    borderBottomWidth: 1,
    borderBottomColor: '#cbd5e1',
    borderStyle: 'dashed',
    marginHorizontal: 8,
  },

  // QR Code
  qrContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginVertical: 8,
  },
  qrImage: {
    width: 120,
    height: 120,
    padding: 4,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 6,
  },
  ticketCode: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#0f172a',
    marginTop: 6,
    letterSpacing: 0.5,
  },

  // Dados do Participante
  infoContainer: {
    paddingHorizontal: 15,
    flex: 1,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  infoLabel: {
    fontSize: 8,
    color: '#64748b',
    textTransform: 'uppercase',
  },
  infoValue: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#1e293b',
    textAlign: 'right',
    maxWidth: '70%',
  },

  // Status Badge
  statusBadge: {
    fontSize: 8,
    fontWeight: 'bold',
    paddingTop: 4,
    paddingBottom: 3,
    paddingHorizontal: 8,
    borderRadius: 4,
    textTransform: 'uppercase',
    textAlign: 'center',
    lineHeight: 1.1,
  },
  statusApproved: {
    backgroundColor: '#dcfce7',
    color: '#15803d',
  },
  statusPending: {
    backgroundColor: '#fef9c3',
    color: '#a16207',
  },

  // Rodapé do Ingresso
  footer: {
    backgroundColor: '#f8fafc',
    padding: 10,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    alignItems: 'center',
  },
  footerText: {
    fontSize: 7,
    color: '#94a3b8',
    textAlign: 'center',
    lineHeight: 1.3,
  }
});

export default function ComprovantePDF({ inscricao }) {
  const codeValue = `INSC_${inscricao.id}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${codeValue}`;
  
  const statusLower = String(inscricao.status || '').toLowerCase();
  const isApproved = statusLower === 'approved' || statusLower === 'credenciado';
  const statusLabel = isApproved 
    ? (statusLower === 'credenciado' ? 'Presença Confirmada' : 'Confirmado')
    : 'Pendente de Pagamento';

  // Formatação do valor em BRL brasileiro
  const valorFormatado = Number(inscricao.valor_pago || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });

  return (
    <Document title={`Comprovante - ${inscricao.nome || 'Inscrição'}`}>
      <Page size="A6" style={styles.page}>
        <View style={styles.ticketContainer}>
          
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.headerText}>Comprovante de Inscrição</Text>
            <Text style={styles.eventTitle} numberOfLines={1}>{inscricao.evento_titulo}</Text>
          </View>

          {/* Detalhes do Evento */}
          <View style={styles.eventDetails}>
            <Text style={styles.eventSubtext}>
              Apresente este ingresso no credenciamento da portaria
            </Text>
          </View>

          {/* QR Code */}
          <View style={styles.qrContainer}>
            <Image style={styles.qrImage} src={qrUrl} />
            <Text style={styles.ticketCode}>#INSC_{inscricao.id}</Text>
          </View>

          {/* Linha Serrilhada */}
          <View style={styles.stubLineContainer}>
            <View style={styles.stubDotLeft} />
            <View style={styles.stubDashed} />
            <View style={styles.stubDotRight} />
          </View>

          {/* Informações do Participante */}
          <View style={styles.infoContainer}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Participante</Text>
              <Text style={styles.infoValue} numberOfLines={1}>{inscricao.nome}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>CPF</Text>
              <Text style={styles.infoValue}>{inscricao.cpf || '-'}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Lote</Text>
              <Text style={styles.infoValue}>{inscricao.lote_nome}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Valor</Text>
              <Text style={styles.infoValue}>{valorFormatado}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Status</Text>
              <Text style={[styles.statusBadge, isApproved ? styles.statusApproved : styles.statusPending]}>
                {statusLabel}
              </Text>
            </View>
          </View>

          {/* Rodapé */}
          <View style={styles.footer}>
            <Text style={styles.footerText}>
              Este comprovante é nominal e intransferível.
            </Text>
            <Text style={[styles.footerText, { marginTop: 2 }]}>
              IAC Buritama • Gerado em {new Date().toLocaleDateString('pt-BR')}
            </Text>
          </View>

        </View>
      </Page>
    </Document>
  );
}
